/**
 * Feature E03-F06 — "Salary benchmark search" FRONT-END model (T03 context
 * collection + T04 result parsing). Pure / DOM-free so the React button
 * (`SalaryRateButton.tsx`) and the unit tests (`salaryRateModel.test.ts`) share
 * one set of rules — no logic in the JSX.
 *
 * 1:1 port of `../EER app/public/assets/js/project-kickoff.js`:
 *   - `searchSalaryRate` ~540-547: collect the search context from the
 *     questionnaire answers by keyword (`getAnswer`) + the project `role_name`.
 *   - `searchSalaryRate` ~556-571: read the LLM result robustly
 *     (`res.salary_rate || res.result || res.answer`), strip ``` fences, treat an
 *     empty result as "No salary data found".
 *
 * The keyword-collection mirrors the legacy fallback chains verbatim:
 *   roleName    = project.role_name || answer('job title')   || answer('target job')
 *   location    = answer('location') || answer('preferred location')
 *   experience  = answer('experience') || answer('years of experience')
 *   industry    = answer('industry')  || answer('target industr')
 *
 * The service-side placeholder defaults ('this position' / 'unspecified location'
 * / …) are NOT applied here — the route/service fill them (SR: Build Prompt). The
 * front only sends what it found; an empty string is fine (the service defaults
 * it). The default-substitution rule is covered by the shared
 * `SALARY_SEARCH_DEFAULTS` and exercised in the tests for completeness (T04
 * acceptance: default substitution).
 */
import {
  SALARY_SEARCH_DEFAULTS,
  type SalarySearchParams,
  type SalarySearchResponse,
} from '@eer/shared';

/** A questionnaire row as the front-end model carries it (text + answer). */
export interface SalaryContextQuestion {
  readonly text?: string;
  readonly answer?: string;
}

/**
 * Find the first answer whose question text contains `keyword`
 * (case-insensitive), mirroring the legacy `getAnswer` closure
 * (`questions.find(q => q.text.toLowerCase().includes(keyword))`). Returns ''
 * when no question matches or its answer is empty.
 */
export function findAnswerByKeyword(
  questions: readonly SalaryContextQuestion[],
  keyword: string,
): string {
  const needle = keyword.toLowerCase();
  const hit = questions.find((q) => (q.text ?? '').toLowerCase().includes(needle));
  return (hit?.answer ?? '').trim();
}

/**
 * Collect the four salary-search params from the questionnaire answers + the
 * project role (`searchSalaryRate` ~544-547). Empty fields are left as empty
 * strings (the service applies {@link SALARY_SEARCH_DEFAULTS}); this keeps the
 * front-end honest about "found vs default".
 */
export function collectSalarySearchParams(
  questions: readonly SalaryContextQuestion[],
  projectRoleName?: string | null,
): SalarySearchParams {
  const role = (projectRoleName ?? '').trim();
  const roleName =
    role ||
    findAnswerByKeyword(questions, 'job title') ||
    findAnswerByKeyword(questions, 'target job');
  const location =
    findAnswerByKeyword(questions, 'location') ||
    findAnswerByKeyword(questions, 'preferred location');
  const experience =
    findAnswerByKeyword(questions, 'experience') ||
    findAnswerByKeyword(questions, 'years of experience');
  const industry =
    findAnswerByKeyword(questions, 'industry') || findAnswerByKeyword(questions, 'target industr');
  return { roleName, location, experience, industry };
}

/**
 * Apply the legacy `SR: Build Prompt` placeholder defaults to a collected param
 * bag (empty/whitespace → the {@link SALARY_SEARCH_DEFAULTS} sentinel). The route
 * already does this server-side; this mirror exists so the front-end can preview
 * exactly what the service will substitute and so T04 can assert the default
 * substitution rule from the same source of truth.
 */
export function applySalaryDefaults(params: SalarySearchParams = {}): Required<SalarySearchParams> {
  const pick = (v: string | undefined, fallback: string): string => {
    const trimmed = typeof v === 'string' ? v.trim() : '';
    return trimmed.length > 0 ? trimmed : fallback;
  };
  return {
    roleName: pick(params.roleName, SALARY_SEARCH_DEFAULTS.roleName),
    location: pick(params.location, SALARY_SEARCH_DEFAULTS.location),
    experience: pick(params.experience, SALARY_SEARCH_DEFAULTS.experience),
    industry: pick(params.industry, SALARY_SEARCH_DEFAULTS.industry),
  };
}

/** Strip markdown code fences and trim (mirror of `stripSalaryFences`, SR side). */
export function stripSalaryFences(text: string | null | undefined): string {
  if (!text) return '';
  return String(text)
    .replace(/```\s*/g, '')
    .trim();
}

/** The outcome of reading a salary-search response into a usable answer string. */
export interface SalaryResult {
  /** True when a non-empty salary string was extracted. */
  readonly ok: boolean;
  /** The cleaned salary text to write into the answer (empty when !ok). */
  readonly salaryRate: string;
  /** The error/notice message to toast when !ok (legacy "No salary data found"). */
  readonly error?: string;
  /**
   * Machine-readable reason of the negative outcome when the API supplied one
   * ('SALARY_NOT_FOUND' / 'SALARY_PROMPT_MISSING' — E12-F06-T04). Branch on THIS,
   * never on the wording.
   */
  readonly code?: string;
}

/**
 * Read the salary-search response into a result the UI can act on
 * (`searchSalaryRate` ~556-571). Branches:
 *   1. `res.found === false` → the honest NEGATIVE outcome of a successful call
 *      (E12-F06-T04): propagate `res.message` (or the deprecated `res.error`
 *      alias); `res.code` is the machine-readable reason. This arm is REACHABLE
 *      again — the body no longer wears the legacy `success:false` envelope that
 *      made `toApiResult` demote it to a transport error.
 *   2. legacy tolerance: `res.success === false` → same treatment (historical
 *      payloads / an old bundle talking to a new API).
 *   3. otherwise take `res.salary_rate || res.result || res.answer`, strip ```
 *      fences; an empty value → ok:false with 'No salary data found'.
 *
 * Accepts the typed {@link SalarySearchResponse} OR any loose object (the legacy
 * payload could carry `result`/`answer` aliases the typed union does not), so the
 * UI is tolerant of every historical shape.
 */
export function parseSalaryResult(
  res: SalarySearchResponse | Record<string, unknown> | null | undefined,
): SalaryResult {
  if (!res || typeof res !== 'object') {
    return { ok: false, salaryRate: '', error: 'No salary data found' };
  }
  const r = res as Record<string, unknown>;
  if (r.found === false || r.success === false) {
    const text = [r.message, r.error].find((v) => typeof v === 'string' && v.length > 0);
    const code = typeof r.code === 'string' ? r.code : undefined;
    return {
      ok: false,
      salaryRate: '',
      error: (text as string | undefined) ?? 'Salary search error',
      ...(code === undefined ? {} : { code }),
    };
  }
  const raw =
    (typeof r.salary_rate === 'string' && r.salary_rate) ||
    (typeof r.result === 'string' && r.result) ||
    (typeof r.answer === 'string' && r.answer) ||
    '';
  const salaryRate = stripSalaryFences(raw);
  if (salaryRate.length === 0) {
    return { ok: false, salaryRate: '', error: 'No salary data found' };
  }
  return { ok: true, salaryRate };
}

/** Success/failure toast copy (legacy notifications, T03 acceptance #3). */
export const SALARY_TOAST = {
  found: 'Market salary rate found',
  none: 'No salary data found',
  error: 'Error searching salary rate',
} as const;
